import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles, Box, Home } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import AuthService from '@/api/auth.service';
import { Checkbox } from "@/components/ui/checkbox"
import LogoWithSlogan from "../../public/images/logo_with_slogan.png";
import { useSearchParams } from 'react-router-dom';
import { useBrand } from '@/lib/brand';

export default function Login() {
  const brand = useBrand();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activationNeeded, setActivationNeeded] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isActivating, setIsActivating] = useState(false);
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [activationCode, setActivationCode] = useState('');

  const { toast } = useToast();
  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  useEffect(() => {
    const activationNeededParam = searchParams.get('activationNeeded');
    const emailAddressParam = searchParams.get('emailAddress');
    if (activationNeededParam === 'true' && emailAddressParam) {
      setActivationNeeded(true);
      setEmailAddress(emailAddressParam);
    }
  }, [searchParams]);

  const onSubmit = async () => {
    if (activationNeeded) {
      if (!activationCode) {
        showToastWithTimeout("error", "Activation Failed", "Please enter the activation code.", 3000);
        return;
      }
      if (!emailAddress) {
        showToastWithTimeout("error", "Login Failed", "Please enter your email", 3000);
        return;
      }
      setIsActivating(true);
      try {
        const response = await AuthService.verifyEmailByCode({
          email: emailAddress,
          activationCode: activationCode,
        });
        if (response.data.result === "success") {
          showToastWithTimeout("success", "Activation Successful", "Your account has been activated.", 3000);
          setActivationNeeded(false);
        } else {
          showToastWithTimeout("error", "Activation Failed", response.data.message, 3000);
        }
      } catch (error) {
        showToastWithTimeout("error", "Activation Failed", "Invalid activation code.", 3000);
      } finally {
        setIsActivating(false);
      }
    } else {
      setIsLoggingIn(true);
      try {
        const response = await AuthService.login({ email, password, rememberMe });
        console.log("login response.data", response.data);
        if (response.data.result === "success") {
          showToastWithTimeout("success", "Login Successful", "You have successfully logged in.", 3000);
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          localStorage.setItem('businesses', JSON.stringify(response.data.businesses));
          localStorage.setItem('selectedBusiness', JSON.stringify(response.data.businesses[0]));
          localStorage.setItem('currentView', 'dashboard');
          localStorage.setItem('modules', JSON.stringify(response.data.modules));
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
        } else {
          showToastWithTimeout("error", "Login Failed", response.data.message, 3000);
        }
      } catch (error) {
        showToastWithTimeout("error", "Login Failed", "Invalid email or password.", 3000);
      } finally {
        setIsLoggingIn(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSubmit();
    }
  };

  // Get brand logo icon
  const getBrandIcon = () => {
    // Check if brand has a custom logo URL first
    if (brand.logo.url) {
      return (
        <img
          alt={brand.name}
          src={brand.logo.url}
          className="h-32 w-auto object-contain"
          style={{
            width: '100%',
            height: 'auto',
          }}
        />
      );
    }
    
    if (brand.logo.icon === 'SparklesIcon') {
      return <Sparkles className="h-16 w-16 text-primary" />;
    } else if (brand.logo.icon === 'BoxIcon') {
      return <Box className="h-16 w-16 text-primary" />;
    } else if (brand.logo.icon === 'HomeIcon') {
      return <Home className="h-16 w-16 text-primary" />;
    }
    return (
      <img
        alt={brand.name}
        src={LogoWithSlogan}
        className="h-32 w-auto"
        style={{
          width: '100%',
          height: 'auto',
        }}
      />
    );
  };

  return (
    <div className="flex min-h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="flex flex-1 flex-col justify-center px-6 py-12 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="mb-10 text-center">
            {getBrandIcon()}
            {brand.logo.icon !== 'SparklesIcon' && brand.logo.icon !== 'BoxIcon' && brand.logo.icon !== 'HomeIcon' && (
              <h1 className="text-3xl font-bold text-primary mt-4">{brand.name}</h1>
            )}
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">
              {activationNeeded ? 'Activate your account' : 'Welcome back'}
            </h2>
            <p className="text-sm text-gray-500">
              {activationNeeded 
                ? 'Enter the activation code sent to your email' 
                : brand.tagline}
            </p>

            <div className="space-y-6">
              {activationNeeded ? (
                <>
                  <div className="space-y-2">
                    <label htmlFor="activationCode" className="text-sm font-medium text-gray-700">
                      Activation Code
                    </label>
                    <div className="relative">
                      <Input
                        id="activationCode"
                        value={activationCode}
                        onChange={(e) => setActivationCode(e.target.value)}
                        onKeyPress={handleKeyPress}
                        type="text"
                        required
                        className="pl-10 h-11 bg-white border-gray-200 focus:border-primary focus:ring-primary transition-all duration-200"
                        placeholder="Enter your activation code"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={onSubmit}
                    className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md shadow-sm transition-all duration-200 flex items-center justify-center gap-2"
                    disabled={isActivating}
                  >
                    {isActivating ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Activate Account
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">
                      Email address
                    </label>
                    <div className="relative">
                      <Input
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleKeyPress}
                        type="email"
                        required
                        autoComplete="email"
                        className="pl-10 h-11 bg-white border-gray-200 focus:border-primary focus:ring-primary transition-all duration-200"
                        placeholder="name@company.com"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor="password" className="text-sm font-medium text-gray-700">
                        Password
                      </label>
                      <a href="/forgot-password" className="text-sm font-medium text-primary hover:text-primary/80">
                        Forgot password?
                      </a>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        type={showPassword ? "text" : "password"}
                        required
                        autoComplete="current-password"
                        className="pl-10 h-11 bg-white border-gray-200 focus:border-primary focus:ring-primary transition-all duration-200"
                        placeholder="••••••••"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-400" />
                      </div>
                      <div
                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Checkbox
                      id="remember-me"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                      Remember me
                    </label>
                  </div>

                  <Button
                    type="button"
                    onClick={onSubmit}
                    className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md shadow-sm transition-all duration-200 flex items-center justify-center gap-2"
                    disabled={isLoggingIn}
                  >
                    {isLoggingIn ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        Sign in
                        <ArrowRight className="h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <span className="text-sm text-gray-500">
                      Don't have an account?{' '}
                      <a href="/signup" className="font-medium text-primary hover:text-primary/80">
                        Sign up
                      </a>
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="relative hidden lg:block lg:flex-1">
        <img
          src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80"
          alt="Financial dashboard"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/70 to-secondary/40 flex flex-col justify-center px-12">
          <div className="max-w-xl text-white">
            <h2 className="text-3xl font-bold mb-4">{brand.tagline}</h2>
            <p className="text-lg text-white/90">
              {brand.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}